import { useCallback } from 'react'
import { createSelector } from 'reselect'
import { useDispatch, useSelector } from '.'
import { ApplicationDataFull } from '../../../types/ApplicationData'
import { VariableModuleData, LightingHeadData, ServiceData } from '../../../types/ModuleData'
import { breakpoint } from '../../style'
import { setModeAction } from '../actions/uiActions'
import { AppState } from '../types'
import { DataState } from '../types/dataTypes'
import { ConfiguratorMode } from '../types/uiTypes'

export const currentApplicationSelector = createSelector<
  AppState,
  DataState,
  string,
  boolean,
  ApplicationDataFull | undefined
>(
  (state) => state.data,
  (state) => state.ui.inspiration.application,
  (state) => state.ui.mode === 'expert',
  ({ applications, modules }, currentApplication, isExpertMode) => {
    const application: ApplicationDataFull | undefined =
      applications.find((x) => x.id === currentApplication) || applications[0]
    if (isExpertMode && application) {
      return {
        ...application,
        availableModules: modules
      }
    }
    return application
  }
)

export const useCurrentApplication = (): ApplicationDataFull | undefined => {
  return useSelector(currentApplicationSelector)
}

export const useInspirationDetail = (): {
  detailModule: VariableModuleData | LightingHeadData | ServiceData | undefined
  showDetail: (moduleId?: string) => void
  closeDetail: () => void
} => {
  const currentApplication = useCurrentApplication()
  const detailModule = useSelector((state) => {
    const id = state.ui.inspiration.showDetail
    return currentApplication?.availableModules.find((x) => x.type !== 'SupportingColumn' && x.id === id) as
      | LightingHeadData
      | VariableModuleData
      | undefined
  })

  const dispatch = useDispatch()
  const showDetail = useCallback(
    (moduleId?: string) => {
      dispatch({
        type: 'Ui/Inspiration/ShowDetail',
        moduleId
      })
    },
    [dispatch]
  )
  const closeDetail = useCallback(() => showDetail(), [showDetail])

  return {
    detailModule,
    showDetail,
    closeDetail
  }
}

export const useBreakpoint = (from?: keyof typeof breakpoint, to?: keyof typeof breakpoint): boolean => {
  const bps = Object.keys(breakpoint) as (keyof typeof breakpoint)[]
  const screenWidth = useSelector((state) => state.ui.screenSize.width)

  const minIndex = from ? bps.indexOf(from) : -1
  const minWidth = (minIndex >= 0 ? breakpoint[bps[minIndex]] : undefined) ?? 0
  const maxIndex = to ? bps.indexOf(to) : -1
  const maxWidth = (maxIndex >= 0 ? breakpoint[bps[maxIndex + 1]] : undefined) ?? Infinity

  return screenWidth >= minWidth && screenWidth < maxWidth
}

export const useConfiguratorMode = (): {
  [key in ConfiguratorMode]: boolean
} & {
  mode: ConfiguratorMode
  toggleMode: () => void
} => {
  const mode = useSelector((state) => state.ui.mode)
  const dispatch = useDispatch()

  const toggleMode = useCallback(() => {
    dispatch(setModeAction(mode === 'expert' ? 'inspiration' : 'expert'))
  }, [mode, dispatch])

  return {
    mode,
    expert: mode === 'expert',
    inspiration: mode === 'inspiration',
    toggleMode
  }
}
